import Paper from "@mui/material/Paper";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import Grid from "@mui/material/Grid2";
import { Stack } from "@mui/system";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSelector } from "react-redux";
import { LOOKUP_FIELD_OPTIONS } from "../../../../../constants/lookupFieldOptionsDC";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setIsLookupFieldsValid,
  setIsPreviewEmpty,
  setLookupFieldDataForPayload,
} from "../../../../../features/uniqueDefSlice";
import PreviewPanel from "../helpers/components/PreviewPanel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { buildLookupPayload } from "../helpers/functions/payloadBuilder";

const CreateNewDefinitionLookupFields = forwardRef((_, ref) => {
  const { selectedDestinationTableCreateScreen } = useSelector(
    (state: any) => state.uniqueDefinition,
  );

  const dispatch = useDispatch();
  const destTableID: any =
    selectedDestinationTableCreateScreen &&
    selectedDestinationTableCreateScreen?.id;

  const termsList = LOOKUP_FIELD_OPTIONS[destTableID] ?? [];

  const [selectedTerms, setSelectedTerms] = useState<any[]>([]);
  const [inputValues, setInputValues] = useState({}) as any;
  const [previewValues, setPreviewValues] = useState({});

  useEffect(() => {
    dispatch(setIsPreviewEmpty(Object.keys(previewValues).length === 0));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewValues]);

  const handleInputChange = (fieldKey: string, value: string) => {
    setInputValues((prev: any) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const updateReduxState = (updatedInputValues: any) => {
    if (Object.keys(updatedInputValues).length === 0) {
      // If no input values, clear the Redux state
      dispatch(setLookupFieldDataForPayload(""));
      return;
    }

    // Construct and format the payload
    const jsonPayload: any = {};
    for (const key in updatedInputValues) {
      if (updatedInputValues[key]) {
        // Only include non-empty values
        jsonPayload[key] = { lookup_field: updatedInputValues[key] };
      }
    }

    if (Object.keys(jsonPayload).length === 0) {
      dispatch(setLookupFieldDataForPayload(""));
    } else {
      const formattedPayload = JSON.stringify(jsonPayload).replace(/"/g, '"');
      dispatch(
        setLookupFieldDataForPayload(`{${formattedPayload.slice(1, -1)}}`),
      );
    }
  };

  const handleRefreshPreview = () => {
    setPreviewValues({ ...inputValues });
    // Update Redux state when refresh is clicked
    updateReduxState(inputValues);
    dispatch(setIsPreviewEmpty(Object.keys(inputValues).length === 0));
  };
  useEffect(() => {
    // whenever the destination table ID changes, clear selections
    setSelectedTerms([]);
    setInputValues({});
    setPreviewValues({});
    dispatch(setIsPreviewEmpty(true));
    dispatch(setLookupFieldDataForPayload("")); // clear Redux payload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destTableID]);

  const previewJsonString =
    JSON.stringify(
      Object.fromEntries(
        Object.entries(previewValues).map(([key, val]) => [
          key,
          { lookup_field: val },
        ]),
      ),
      null,
      2,
    ) || "{ Preview Demo }";

  const computeLookupValidity = (
    selectedTerms: any[],
    inputValues: Record<string, string>,
  ) => {
    if (selectedTerms.length === 0) return false;

    return selectedTerms.every(
      (term) =>
        typeof inputValues[term.title] === "string" &&
        inputValues[term.title].trim() !== "",
    );
  };

  useEffect(() => {
    const isValid = computeLookupValidity(selectedTerms, inputValues);
    dispatch(setIsLookupFieldsValid(isValid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerms, inputValues]);

  useImperativeHandle(ref, () => ({
    refresh: () => {
      const payload = buildLookupPayload(inputValues);

      dispatch(setLookupFieldDataForPayload(payload ?? ""));
      dispatch(setIsPreviewEmpty(!payload));

      return payload;
    },
  }));

  return (
    <>
      <Paper sx={{ mb: 1, pb: 2 }}>
        <Box
          sx={{ paddingTop: "8px", paddingLeft: "16px", paddingRight: "16px" }}
        >
          <Grid
            container
            alignItems={"flex-start"}
            justifyContent={"flex-start"}
            //  display={"flex"}
          >
            <Grid size={12}>
              <Stack width={"100%"}>
                <Typography variant="h6" sx={{ fontSize: "20px", mb: 3 }}>
                  Lookup Fields (Group By)
                </Typography>
                <Autocomplete
                  data-tracker="300"
                  multiple
                  size="small"
                  fullWidth
                  options={termsList}
                  getOptionLabel={(option) => option.title}
                  isOptionEqualToValue={(option, value) =>
                    option.title === value.title
                  }
                  filterSelectedOptions
                  value={selectedTerms}
                  onChange={(_, newValue) => {
                    // Determine removed terms
                    const removed = selectedTerms.filter(
                      (term) =>
                        !newValue.some(
                          (newTerm) => newTerm.title === term.title,
                        ),
                    );

                    // Remove from inputValues and update Redux immediately
                    const updatedInputValues = { ...inputValues };
                    removed.forEach((term) => {
                      delete updatedInputValues[term.title];
                    });

                    setSelectedTerms(newValue);
                    setInputValues(updatedInputValues);

                    // Update Redux state immediately when chips are removed
                    updateReduxState(updatedInputValues);

                    // Also update preview values to reflect the change
                    const updatedPreviewValues: any = { ...previewValues };
                    removed.forEach((term) => {
                      delete updatedPreviewValues[term.title];
                    });
                    setPreviewValues(updatedPreviewValues);
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option: any, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.title}
                        label={option.title}
                        sx={{
                          backgroundColor: "#7B1FA2",
                          color: "#ffffff",
                          height: "27px",
                        }}
                        deleteIcon={
                          <CancelIcon
                            sx={{
                              color: "rgba(255, 255, 255, 0.8) !important",
                              fontSize: "16px !important",
                            }}
                          />
                        }
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search field names or choose from the list"
                      label="Terms In Table"
                      color="secondary"
                      sx={{ mb: 2 }}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />
                  )}
                />
                <Grid container spacing={2}>
                  {selectedTerms?.map((term) => (
                    <Grid size={4} key={term.title}>
                      <TextField
                        key={term.title}
                        fullWidth
                        label={term.title}
                        placeholder={`Enter value for ${term.title}`}
                        color="secondary"
                        size="small"
                        sx={{ mb: 1 }}
                        value={inputValues[term.title] || ""}
                        onChange={(e) =>
                          handleInputChange(term.title, e.target.value)
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Accordion>
                <AccordionSummary
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      alignItems: "center",
                      margin: 0,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ExpandMoreIcon />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "normal", fontSize: 15 }}
                    >
                      Preview
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    gap: 2,
                    display: "flex",
                    flexDirection: "column",
                    ml: 3,
                  }}
                >
                  <PreviewPanel
                    json={previewJsonString || "{ Preview Demo }"}
                    onRefresh={handleRefreshPreview}
                    // title="Definition - Preview"
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </>
  );
});

export default CreateNewDefinitionLookupFields;
